// Evidence & assurance (APP-004 R3 P2, UI-P4).
//
// Types are transcribed from the frozen Policy AI contract
// (v0.3.0-governance-compliance). Status values, transition rules,
// segregation-of-duties checks, health classification and manifest hashing all
// belong to the backend: this module carries them across the wire and nothing
// more. The browser never recomputes a status or a hash.
//
// Binary content never passes through here. A file-backed version references a
// Documents Platform record by id; the alternatives are an external URL or
// attestation text.
import { GovernanceHttp, queryString } from "./governanceHttp";
import type { GovernanceClientOptions } from "./governanceHttp";

export type EvidenceCollectionStatus = "open" | "closed";
export type EvidenceRequestStatus = "open" | "submitted" | "accepted" | "rejected" | "cancelled";
export type EvidenceStatus = "draft" | "submitted" | "approved" | "rejected" | "expired" | "superseded";
export type EvidenceSource = "upload" | "url" | "attestation" | "system_export" | "policy_artifact";
export type EvidenceSensitivity = "public" | "internal" | "confidential" | "restricted";
export type EvidenceDecision = "submitted" | "approved" | "rejected" | "reopened" | "superseded" | "expired";
export type EvidenceLinkTarget =
  | "requirement"
  | "mapping"
  | "policy"
  | "policy_version"
  | "exception"
  | "compensating_control"
  | "audit_project"
  | "finding";
export type EvidenceLinkRole = "supports" | "refutes" | "context";
export type EvidenceHealthKind = "missing" | "stale" | "duplicate" | "conflicting";
export type EvidenceExportFormat = "csv" | "manifest_json";

export interface EvidenceCollection {
  id: string;
  organizationId: string;
  name: string;
  description: string;
  ownerId: string;
  frameworkVersionId: string | null;
  status: EvidenceCollectionStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface EvidenceRequest {
  id: string;
  organizationId: string;
  collectionId: string;
  requirementId: string | null;
  policyId: string | null;
  title: string;
  instructions: string;
  assigneeId: string;
  dueAt: string | null;
  status: EvidenceRequestStatus;
  requestedBy: string;
  schedulerJobId: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

export interface EvidenceItem {
  id: string;
  organizationId: string;
  collectionId: string;
  requestId: string | null;
  title: string;
  evidenceType: string;
  source: EvidenceSource;
  ownerId: string;
  sensitivity: EvidenceSensitivity;
  effectiveStart: string | null;
  effectiveEnd: string | null;
  reviewAt: string | null;
  status: EvidenceStatus;
  currentVersionId: string | null;
  supersededById: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface EvidenceVersion {
  id: string;
  organizationId: string;
  evidenceItemId: string;
  versionNumber: number;
  documentId: string | null;
  externalUrl: string | null;
  attestationText: string;
  /** Computed by the backend over the referenced content. Never recomputed here. */
  contentHash: string;
  byteSize: number;
  capturedAt: string;
  capturedBy: string;
  createdAt: string;
}

export interface EvidenceApproval {
  id: string;
  organizationId: string;
  evidenceItemId: string;
  evidenceVersionId: string | null;
  actorId: string;
  decision: EvidenceDecision;
  rationale: string;
  fromStatus: string;
  toStatus: string;
  decidedAt: string;
}

export interface EvidenceLink {
  id: string;
  organizationId: string;
  evidenceItemId: string;
  targetType: EvidenceLinkTarget;
  targetId: string;
  linkRole: EvidenceLinkRole;
  createdBy: string;
  createdAt: string;
}

export interface EvidenceHealthFinding {
  kind: EvidenceHealthKind;
  /**
   * The backend reports "requirement" for stale and duplicate findings even
   * though their targets are an evidence item and a content hash. Read `kind`
   * and `label`, not this field, when presenting a finding.
   */
  targetType: EvidenceLinkTarget | "requirement";
  targetId: string;
  label: string;
  detail: string;
  evidenceItemIds: string[];
}

export interface EvidenceHealthReport {
  generatedAt: string;
  counts: Record<EvidenceHealthKind, number>;
  findings: EvidenceHealthFinding[];
}

export interface EvidenceAgingRunResult {
  expired: number;
  dueForReview: number;
  requestsOverdue: number;
  notified: number;
}

export interface EvidencePackage {
  id: string;
  organizationId: string;
  name: string;
  frameworkVersionId: string | null;
  collectionId: string | null;
  scope: Record<string, unknown>;
  manifestHash: string;
  itemCount: number;
  generatedBy: string;
  generatedAt: string;
}

export interface EvidencePackageEntry {
  evidenceItemId: string;
  evidenceVersionId: string;
  title: string;
  versionNumber: number;
  contentHash: string;
  position: number;
}

export interface EvidencePackageManifest {
  manifestVersion: string;
  packageId: string;
  organizationId: string;
  name: string;
  frameworkVersionId: string | null;
  collectionId: string | null;
  scope: Record<string, unknown>;
  itemCount: number;
  entries: EvidencePackageEntry[];
  manifestHash: string;
}

export interface EvidencePackageExport {
  id: string;
  organizationId: string;
  packageId: string;
  format: EvidenceExportFormat;
  manifestHash: string;
  contentHash: string;
  byteSize: number;
  exportedBy: string;
  createdAt: string;
}

/** A Documents Platform record already held by the tenant. */
export interface PolicyDocument {
  id: string;
  organizationId: string;
  policyId: string;
  filename: string;
  mimeType: string;
  byteSize: number;
  checksumSha256: string;
  storageRef: string;
  pageCount: number;
  ocrStatus: string;
  status: "processed" | "failed";
  source: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEvidenceCollectionInput {
  name: string;
  description?: string;
  ownerId: string;
  frameworkVersionId?: string | null;
}

export interface CreateEvidenceRequestInput {
  title: string;
  instructions?: string;
  assigneeId: string;
  requirementId?: string | null;
  policyId?: string | null;
  dueAt?: string | null;
}

export interface CreateEvidenceItemInput {
  collectionId: string;
  requestId?: string | null;
  title: string;
  evidenceType?: string;
  source?: EvidenceSource;
  ownerId: string;
  sensitivity?: EvidenceSensitivity;
  effectiveStart?: string | null;
  effectiveEnd?: string | null;
  reviewAt?: string | null;
}

/**
 * One of the three content references. The backend rejects a version that
 * carries none of them, and no file bytes are ever sent from the browser.
 */
export interface AddEvidenceVersionInput {
  documentId?: string | null;
  externalUrl?: string | null;
  attestationText?: string;
}

export interface EvidenceItemFilter {
  collectionId?: string;
  requestId?: string;
  ownerId?: string;
  status?: EvidenceStatus;
}

/** The two formats Policy AI renders. XLSX and PDF need the Reporting platform. */
export const EVIDENCE_EXPORT_FORMATS: EvidenceExportFormat[] = ["csv", "manifest_json"];

/**
 * Formats the UI shows as unavailable rather than hiding, so an auditor asking
 * for a spreadsheet sees why it cannot be produced yet.
 */
export const EVIDENCE_UNSUPPORTED_EXPORT_FORMATS = ["xlsx", "pdf"] as const;

export class EvidenceClient {
  private http: GovernanceHttp;

  constructor(opts: GovernanceClientOptions | GovernanceHttp) {
    this.http = opts instanceof GovernanceHttp ? opts : new GovernanceHttp(opts);
  }

  // ----- collections --------------------------------------------------------
  collections(orgId: string) {
    return this.http.get<{ collections: EvidenceCollection[] }>(orgId, "evidence-collections");
  }
  collection(orgId: string, collectionId: string) {
    return this.http.get<{ collection: EvidenceCollection }>(
      orgId,
      `evidence-collections/${collectionId}`,
    );
  }
  createCollection(orgId: string, input: CreateEvidenceCollectionInput) {
    return this.http.post<{ collection: EvidenceCollection }>(orgId, "evidence-collections", input);
  }

  // ----- requests -----------------------------------------------------------
  requests(orgId: string, filter: { collectionId?: string; status?: EvidenceRequestStatus } = {}) {
    return this.http.get<{ requests: EvidenceRequest[] }>(
      orgId,
      `evidence-requests${queryString({ ...filter })}`,
    );
  }
  createRequest(orgId: string, collectionId: string, input: CreateEvidenceRequestInput) {
    return this.http.post<{ request: EvidenceRequest }>(
      orgId,
      `evidence-collections/${collectionId}/requests`,
      input,
    );
  }
  /** Accepting and rejecting need `policy_ai.evidence.approve`; cancelling does not. */
  acceptRequest(orgId: string, requestId: string) {
    return this.http.post<{ request: EvidenceRequest }>(orgId, `evidence-requests/${requestId}/accept`);
  }
  rejectRequest(orgId: string, requestId: string) {
    return this.http.post<{ request: EvidenceRequest }>(orgId, `evidence-requests/${requestId}/reject`);
  }
  cancelRequest(orgId: string, requestId: string) {
    return this.http.post<{ request: EvidenceRequest }>(orgId, `evidence-requests/${requestId}/cancel`);
  }

  // ----- items --------------------------------------------------------------
  items(orgId: string, filter: EvidenceItemFilter = {}) {
    return this.http.get<{ items: EvidenceItem[] }>(orgId, `evidence-items${queryString({ ...filter })}`);
  }
  item(orgId: string, itemId: string) {
    return this.http.get<{ item: EvidenceItem; versions: EvidenceVersion[]; links: EvidenceLink[] }>(
      orgId,
      `evidence-items/${itemId}`,
    );
  }
  createItem(orgId: string, input: CreateEvidenceItemInput) {
    return this.http.post<{ item: EvidenceItem }>(orgId, "evidence-items", input);
  }

  // ----- versions and history ----------------------------------------------
  versions(orgId: string, itemId: string) {
    return this.http.get<{ versions: EvidenceVersion[] }>(orgId, `evidence-items/${itemId}/versions`);
  }
  /** Capturing content on an approved item returns it to draft — the backend decides. */
  addVersion(orgId: string, itemId: string, input: AddEvidenceVersionInput) {
    return this.http.post<{ version: EvidenceVersion; item: EvidenceItem }>(
      orgId,
      `evidence-items/${itemId}/versions`,
      input,
    );
  }
  history(orgId: string, itemId: string) {
    return this.http.get<{ history: EvidenceApproval[] }>(orgId, `evidence-items/${itemId}/history`);
  }

  // ----- lifecycle ----------------------------------------------------------
  private decide(orgId: string, itemId: string, action: string, body: Record<string, unknown>) {
    return this.http.post<{ item: EvidenceItem; approval: EvidenceApproval }>(
      orgId,
      `evidence-items/${itemId}/${action}`,
      body,
    );
  }
  submitItem(orgId: string, itemId: string, rationale = "") {
    return this.decide(orgId, itemId, "submit", { rationale });
  }
  /**
   * Approval is refused with 409 when the backend finds a segregation-of-duties
   * conflict (owner, creator, capturer or submitter). That check is not
   * mirrored here.
   */
  approveItem(orgId: string, itemId: string, rationale = "") {
    return this.decide(orgId, itemId, "approve", { rationale });
  }
  rejectItem(orgId: string, itemId: string, rationale = "") {
    return this.decide(orgId, itemId, "reject", { rationale });
  }
  reopenItem(orgId: string, itemId: string, rationale = "") {
    return this.decide(orgId, itemId, "reopen", { rationale });
  }
  supersedeItem(orgId: string, itemId: string, supersededById: string, rationale = "") {
    return this.decide(orgId, itemId, "supersede", { rationale, supersededById });
  }
  expireItem(orgId: string, itemId: string, rationale = "") {
    return this.decide(orgId, itemId, "expire", { rationale });
  }

  // ----- links --------------------------------------------------------------
  links(orgId: string, itemId: string) {
    return this.http.get<{ links: EvidenceLink[] }>(orgId, `evidence-items/${itemId}/links`);
  }
  addLink(
    orgId: string,
    itemId: string,
    input: { targetType: EvidenceLinkTarget; targetId: string; linkRole?: EvidenceLinkRole },
  ) {
    return this.http.post<{ link: EvidenceLink }>(orgId, `evidence-items/${itemId}/links`, input);
  }
  removeLink(orgId: string, itemId: string, linkId: string) {
    return this.http.del<null>(orgId, `evidence-items/${itemId}/links/${linkId}`);
  }

  // ----- assurance ----------------------------------------------------------
  health(orgId: string, frameworkVersionId?: string) {
    return this.http.get<{ health: EvidenceHealthReport }>(
      orgId,
      `evidence/health${queryString({ frameworkVersionId })}`,
    );
  }
  runAging(orgId: string) {
    return this.http.post<{ result: EvidenceAgingRunResult }>(orgId, "evidence/aging/run", {});
  }

  // ----- packages and exports ----------------------------------------------
  packages(orgId: string) {
    return this.http.get<{ packages: EvidencePackage[] }>(orgId, "evidence-packages");
  }
  createPackage(
    orgId: string,
    input: { name: string; collectionId?: string | null; frameworkVersionId?: string | null },
  ) {
    return this.http.post<{ package: EvidencePackage }>(orgId, "evidence-packages", input);
  }
  manifest(orgId: string, packageId: string) {
    return this.http.get<{ manifest: EvidencePackageManifest }>(
      orgId,
      `evidence-packages/${packageId}/manifest`,
    );
  }
  /** Returns the rendered body inline with its audit record, as the backend does. */
  exportPackage(orgId: string, packageId: string, format: EvidenceExportFormat = "manifest_json") {
    return this.http.post<{ export: EvidencePackageExport; contentType: string; content: string }>(
      orgId,
      `evidence-packages/${packageId}/export`,
      { format },
    );
  }
  exports(orgId: string, packageId: string) {
    return this.http.get<{ exports: EvidencePackageExport[] }>(
      orgId,
      `evidence-packages/${packageId}/exports`,
    );
  }

  // ----- Documents boundary -------------------------------------------------
  /**
   * Documents already imported against a policy. Evidence references one by id;
   * Policy AI stores no bytes of its own and the browser uploads nothing here,
   * so a file-backed version requires a Documents record to exist first.
   */
  policyDocuments(orgId: string, policyId: string) {
    return this.http.get<{ documents: PolicyDocument[] }>(orgId, `policies/${policyId}/documents`);
  }

  /**
   * Imports a document the shared Documents Platform already holds, by id. The
   * bytes stay in Documents; Policy AI records what the platform's extraction
   * returned, which is why an unreadable scan is refused here rather than
   * imported as empty text.
   */
  importStoredDocument(
    orgId: string,
    policyId: string,
    input: { documentId: string; versionNumber?: number; title?: string; idempotencyKey?: string },
  ) {
    const { documentId, versionNumber, title, idempotencyKey } = input;
    return this.http.post<{
      job: { id: string; status: string; importedCount: number; failedCount: number };
      items: { id: string; status: string; failureReason: string | null }[];
      replayed: boolean;
    }>(orgId, `policies/${policyId}/documents/import`, {
      ...(idempotencyKey ? { idempotencyKey } : {}),
      files: [
        {
          documentId,
          ...(versionNumber === undefined ? {} : { versionNumber }),
          ...(title ? { title } : {}),
        },
      ],
    });
  }
}
